import { Header, Sidebar } from '@/components/layout';
import { ModeSwitcher } from '@/components/session/ModeSwitcher';
import { RecordingControls } from '@/components/session/RecordingControls';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useRecordingState } from '@/contexts/RecordingContext';
import { InterpreterSessionControls } from '@/features/interpreter/InterpreterSessionControls';
import { InterpreterSettings, type SpeechSettings } from '@/features/interpreter/InterpreterSettings';
import { InterpreterSummaryPanel } from '@/features/interpreter/InterpreterSummaryPanel';
import { recordRequest } from '@/features/interpreter/api';
import { isInterpreterLanguage, labels } from '@/features/interpreter/interpreterPage.constants';
import { interpreterPageStyles as styles } from '@/features/interpreter/interpreterPage.styles';
import { useInterpreter } from '@/features/interpreter/useInterpreter';
import { useAudioDevices } from '@/hooks/useAudioDevices';
import { usePanelResize } from '@/hooks/usePanelResize';
import { apiRequest } from '@/lib/queryClient';
import { useDictationRecordingSignal } from '@/pages/dictationRecordingSignal';
import { Box, Text } from '@careflux/primitives';
import { MicrophoneNotice } from '@careflux/primitives/microphone';
import { useQuery } from '@tanstack/react-query';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'wouter';

const InterpreterWorkspace = ({ id }: { id?: string }) => {
  const [, navigate] = useLocation();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [speaker, setSpeaker] = useState<'doctor' | 'patient'>('doctor');
  const [settings, setSettings] = useState<SpeechSettings>(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('interpreterPlaybackSettings') ?? '{}');
      const clinicianLanguage = saved.clinicianLanguage === 'en' ? 'en' : 'is';
      const patientLanguage =
        isInterpreterLanguage(saved.patientLanguage) && saved.patientLanguage !== clinicianLanguage
          ? saved.patientLanguage
          : 'no';
      return {
        clinicianLanguage,
        patientLanguage,
        speakClinician: (saved.speakClinician ?? saved.speakIcelandic) !== false,
        speakPatient: (saved.speakPatient ?? saved.speakNorwegian) !== false,
      };
    } catch {
      return { clinicianLanguage: 'is', patientLanguage: 'no', speakClinician: true, speakPatient: true };
    }
  });
  useEffect(() => {
    sessionStorage.setItem('interpreterPlaybackSettings', JSON.stringify(settings));
  }, [settings]);
  const devices = useAudioDevices(false);
  const interpreter = useInterpreter(id, { deviceId: devices.selectedDeviceId, ...settings });
  const { setRecording } = useRecordingState();
  useDictationRecordingSignal(
    interpreter.phase !== 'idle' || interpreter.audioError || interpreter.audioSaving,
    setRecording
  );
  const busy = creating || interpreter.phase !== 'idle' || interpreter.audioError || interpreter.audioSaving;
  const resize = usePanelResize('interpreterSummaryWidth');
  const seededSpeaker = useRef(false);
  const processing = !['idle', 'speaking', 'paused'].includes(interpreter.phase);
  const ended = interpreter.record?.config.phase === 'ended';
  useEffect(() => {
    if (!interpreter.record || seededSpeaker.current) return;
    seededSpeaker.current = true;
    const record = interpreter.record;
    const rows = record.utterances.filter((row) => row.turn_id === record.config.turn_id);
    const completed =
      record.config.phase === 'paused' && rows.length > 0 && rows.every((row) => row.translation_status === 'ready');
    setSpeaker(completed ? (record.config.speaker === 'doctor' ? 'patient' : 'doctor') : record.config.speaker);
  }, [interpreter.record]);
  const begin = () => void interpreter.start(speaker);
  const finish = async () => {
    if (await interpreter.stop()) setSpeaker((current) => (current === 'doctor' ? 'patient' : 'doctor'));
  };
  const replay = async () => {
    const previous = interpreter.record?.config.speaker;
    const completingHandoff = interpreter.handoffPending;
    if ((await interpreter.replay()) && completingHandoff) setSpeaker(previous === 'doctor' ? 'patient' : 'doctor');
  };
  const continueWithoutSpeech = async () => {
    const previous = interpreter.record?.config.speaker;
    if (await interpreter.continueWithoutSpeech()) setSpeaker(previous === 'doctor' ? 'patient' : 'doctor');
  };
  const toggleRecording = () => {
    if (interpreter.phase === 'speaking') void interpreter.pauseCapture();
    else if (interpreter.phase === 'paused') interpreter.resumeCapture();
    else begin();
  };
  const create = async () => {
    if (busy) return;
    setCreating(true);
    setCreateError('');
    try {
      const record = await recordRequest('POST', '', {
        clinician_language: settings.clinicianLanguage,
        patient_language: settings.patientLanguage,
      });
      navigate(`/interpreter/${record.id}`);
    } catch {
      setCreateError('Ekki tókst að hefja túlkunarviðtal. Reyndu aftur.');
    } finally {
      setCreating(false);
    }
  };
  const effectiveSettings: SpeechSettings = {
    ...settings,
    clinicianLanguage: interpreter.record?.config.clinician_language ?? settings.clinicianLanguage,
    patientLanguage: interpreter.record?.config.patient_language ?? settings.patientLanguage,
  };
  const settingsControls = (
    <InterpreterSettings
      settings={effectiveSettings}
      onChange={setSettings}
      languagesLocked={Boolean(id)}
      devices={devices.devices}
      deviceId={devices.selectedDeviceId}
      onDeviceChange={devices.setSelectedDeviceId}
      onRefreshDevices={() => void devices.retryMicrophone()}
      disabled={busy}
    />
  );
  return (
    <>
      <Header title="" centerContent={<ModeSwitcher activeMode="interpreter" disabled={busy} />} />
      <main className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <MicrophoneNotice access={devices.microphone} onRetry={devices.retryMicrophone} disabled={busy} />
        {!id ? (
          <Box
            as="section"
            className="mx-auto mt-6 max-w-xl space-y-6 rounded-2xl border bg-card p-6 shadow-sm md:mt-12"
          >
            <Text asChild variant="heading">
              <h1 className="font-semibold text-2xl">
                Túlkunarviðtal <Text className="text-primary text-sm">Beta</Text>
              </h1>
            </Text>
            {settingsControls}
            <Button disabled={busy} onClick={() => void create()}>
              {creating ? 'Hef viðtal…' : 'Hefja túlkunarviðtal'}
            </Button>
          </Box>
        ) : (
          <Box className={styles.liveContainer}>
            <Box className={styles.liveHeader}>
              <Box>
                <Text asChild variant="heading">
                  <h1 className={styles.liveTitle}>
                    Túlkunarviðtal
                    {interpreter.record && (
                      <>
                        {' '}
                        · {labels[interpreter.record.config.clinician_language]} ↔{' '}
                        {labels[interpreter.record.config.patient_language]}
                      </>
                    )}
                  </h1>
                </Text>
                <Text asChild>
                  <p className={styles.mutedCaption}>
                    Aðeins gervigögn í prófun. Talrit vistast. Hljóðvistun fylgir stillingum stofnunarinnar.
                  </p>
                </Text>
              </Box>
              <Box className="flex flex-wrap items-center gap-2">
                {!devices.devices.length && !ended && (
                  <Button variant="outline" disabled={busy} onClick={() => void devices.retryMicrophone()}>
                    Velja hljóðnema
                  </Button>
                )}
                <RecordingControls
                  visualizerEnabled={!ended}
                  audioLevel={interpreter.audioLevel}
                  isRecording={interpreter.phase === 'speaking'}
                  isPaused={interpreter.phase === 'paused'}
                  isInitializing={interpreter.phase === 'starting'}
                  isFinalizingTranscription={processing && interpreter.phase !== 'starting'}
                  elapsedTime={Math.floor(interpreter.elapsed)}
                  devices={devices.devices}
                  selectedDeviceId={devices.selectedDeviceId}
                  onDeviceChange={!busy && !ended ? devices.setSelectedDeviceId : undefined}
                  disabled={
                    ended || !interpreter.record || (interpreter.handoffPending && interpreter.phase === 'idle')
                  }
                  onToggleRecording={toggleRecording}
                  recordingLabel={
                    interpreter.phase === 'speaking'
                      ? 'Gera hlé'
                      : interpreter.phase === 'paused'
                        ? 'Halda áfram'
                        : 'Byrja tal'
                  }
                />
              </Box>
            </Box>
            {interpreter.isLoading ? (
              <output>Sæki viðtal…</output>
            ) : interpreter.isError ? (
              <Text asChild>
                <p role="alert">Ekki tókst að sækja viðtalið.</p>
              </Text>
            ) : (
              interpreter.record && (
                <Box
                  className={styles.columns}
                  style={{ '--summary-width': `${resize.rightPanelWidth}px` } as CSSProperties}
                >
                  <InterpreterSessionControls
                    interpreter={interpreter}
                    speaker={speaker}
                    onSwitchSpeaker={() => setSpeaker((current) => (current === 'doctor' ? 'patient' : 'doctor'))}
                    onBegin={begin}
                    onFinish={() => void finish()}
                    onReplay={() => void replay()}
                    onContinueWithoutSpeech={() => void continueWithoutSpeech()}
                    newInterview={() => navigate('/interpreter')}
                    settings={effectiveSettings}
                    onSettingsChange={setSettings}
                  />
                  <button
                    type="button"
                    aria-label="Breyta breidd samantektar"
                    className={styles.resizeHandle}
                    onMouseDown={resize.handleMouseDown}
                    onKeyDown={(event) => {
                      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                      event.preventDefault();
                      resize.setRightPanelWidth(
                        Math.max(
                          300,
                          Math.min(
                            window.innerWidth * 0.6,
                            resize.rightPanelWidth + (event.key === 'ArrowLeft' ? 32 : -32)
                          )
                        )
                      );
                    }}
                  />
                  <Box className={styles.summaryPanel}>
                    <InterpreterSummaryPanel
                      key={id}
                      record={interpreter.record}
                      busy={busy}
                      onGenerate={interpreter.summarize}
                    />
                  </Box>
                </Box>
              )
            )}
          </Box>
        )}
        {(createError || interpreter.message) && (
          <Text asChild>
            <p
              role="alert"
              className="mx-auto mt-4 max-w-3xl rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-destructive"
            >
              {createError || interpreter.message}
            </p>
          </Text>
        )}
      </main>
    </>
  );
};
const InterpreterPage = () => {
  const { id } = useParams<{ id: string }>();
  const { authAccountId, authOrgId, isViewingAsDelegate } = useAuth();
  const availability = useQuery({
    queryKey: ['interpreter-availability', authAccountId, authOrgId],
    queryFn: async (): Promise<{ enabled: boolean }> =>
      (await apiRequest('GET', '/api/interpreter/availability')).json(),
    enabled: !!authAccountId && !isViewingAsDelegate,
    retry: false,
    staleTime: 30_000,
  });
  return (
    <Box className="flex h-dvh overflow-hidden bg-background">
      <Sidebar />
      <Box className="flex min-w-0 flex-1 flex-col">
        {!isViewingAsDelegate && availability.data?.enabled ? (
          <InterpreterWorkspace key={id ?? 'new'} id={id} />
        ) : (
          <>
            <Header title="Túlkunarviðtal" />
            <output className="p-6">
              {availability.isPending && !isViewingAsDelegate
                ? 'Sæki stillingar…'
                : 'Túlkunarviðtal er ekki virkt fyrir þennan aðgang.'}
            </output>
          </>
        )}
      </Box>
    </Box>
  );
};
export default InterpreterPage;
